import { CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { FC, useId } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { IReservationV2 } from "../../interfaces/reservations/Reservations.types";
import { IReservationPaymentBreakdown } from "../../interfaces/wallet/Wallet.types";
import { cx } from "../../utils/classnames";
import { eur } from "../../utils/eur";
import { TicketStatusEnum } from "../reservations/res.enum";

const STATUS: Record<number, { name: string; className: string; Icon: typeof CheckCircleIcon }> = {
  [TicketStatusEnum.Confirmed]: { name: "I konfirmuar", className: "bg-green", Icon: CheckCircleIcon },
  [TicketStatusEnum.Pending]: { name: "I pa përfunduar", className: "bg-orange", Icon: ClockIcon },
  [TicketStatusEnum.Canceled]: { name: "I anuluar", className: "bg-gray-500", Icon: XCircleIcon },
  [TicketStatusEnum.Refund]: { name: "I rimbursuar", className: "bg-purple-500", Icon: XCircleIcon }
};

const METHODS: Record<number, string> = { 1: "Raiffeisen", 2: "Stripe", 7: "NLB" };

export const paymentMethodName = (paymentMethodId?: number | null): string =>
  !paymentMethodId ? "Gjirafa Money" : METHODS[paymentMethodId] ?? `Metoda ${paymentMethodId}`;

// One chip for the booking's money: total, how it was paid, and the status as colour and icon. An
// unpaid booking has no method yet (its id 0 would read as Gjirafa Money).
const PaymentChip: FC<{
  paymentStatus: IReservationV2["paymentStatus"];
  paymentMethodId: IReservationV2["paymentMethodId"];
  price: IReservationV2["price"];
  breakdown?: IReservationPaymentBreakdown;
}> = ({ paymentStatus, paymentMethodId, price, breakdown }) => {
  const id = `payment-chip-${useId()}`;
  const status = STATUS[paymentStatus];
  const split = !!breakdown && breakdown.paidByCash > 0 && breakdown.paidByCredits > 0;
  const splitText = split
    ? `Kartelë ${eur(breakdown.paidByCash)} · Gjirafa Money ${eur(breakdown.paidByCredits)}`
    : null;
  const tip = [status?.name, splitText].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col items-end gap-1">
      <span
        className={cx([
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[13px] text-white whitespace-nowrap",
          status?.className ?? "bg-gray-500"
        ])}
        data-tooltip-id={id}
        data-tooltip-content={tip}
      >
        {price}€
        {paymentStatus !== TicketStatusEnum.Pending && ` · ${paymentMethodName(paymentMethodId)}`}
        {status && <status.Icon className="w-4 h-4" aria-label={status.name} />}
      </span>
      {tip && <Tooltip id={id} place="left" positionStrategy="fixed" className="z-50 !text-xs" />}
      {!!breakdown && breakdown.paidByCredits > 0 && (
        <span className="flex items-center gap-2 text-xs text-gray-700 whitespace-nowrap">
          {splitText}
          {breakdown.walletId && (
            <Link className="text-primary underline" to={`/wallets/${breakdown.walletId}`}>
              Shiko portofolin
            </Link>
          )}
        </span>
      )}
    </div>
  );
};

export default PaymentChip;
